import React, { type ReactNode, useState } from 'react';
import {
  IconButton,
  type IconButtonProps,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  type MenuProps,
  Tooltip,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';

export type OptionsMenuItem = {
  key?: string;
  label: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  href?: string;
  target?: string;
  rel?: string;
};

export type OptionsMenuProps = {
  items: OptionsMenuItem[];
  ariaLabel?: string;
  buttonProps?: Omit<IconButtonProps, 'onClick' | 'aria-label'>;
  menuProps?: Omit<MenuProps, 'open' | 'anchorEl' | 'onClose'>;
  renderTrigger?: (props: {
    onClick: (e: React.MouseEvent) => void;
  }) => ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
};

export function OptionsMenu({
  items,
  ariaLabel = 'Options',
  buttonProps,
  menuProps,
  renderTrigger,
  onOpen,
  onClose,
}: OptionsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<Element>) => {
    // Stop the click from bubbling to row-level handlers (e.g. SearchPage's
    // row onClick that opens the preview drawer). Without this the row-select
    // re-render resets anchorEl and the menu closes the instant it opens.
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    onOpen?.();
  };

  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  const trigger = renderTrigger ? (
    renderTrigger({ onClick: handleOpen })
  ) : (
    <Tooltip title={ariaLabel}>
      <IconButton aria-label={ariaLabel} onClick={handleOpen} {...buttonProps}>
        <MoreVertIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      {trigger}
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        {...menuProps}
      >
        {items.map((item, idx) => {
          const key = item.key ?? `opt-${idx}`;
          const content = (
            <>
              {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
              <ListItemText>{item.label}</ListItemText>
            </>
          );

          if (item.href) {
            return (
              <MenuItem
                key={key}
                disabled={item.disabled}
                onClick={() => {
                  handleClose();
                  item.onClick?.();
                }}
                component="a"
                href={item.href}
                target={item.target}
                rel={item.rel}
              >
                {content}
              </MenuItem>
            );
          }

          return (
            <MenuItem
              key={key}
              disabled={item.disabled}
              onClick={() => {
                handleClose();
                item.onClick?.();
              }}
            >
              {content}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default OptionsMenu;
